import React, { memo, Suspense, useCallback, useState, useMemo } from 'react'
import { HStack } from '../../../../../../ui/Stack'
import { Modal } from 'antd'
import { ModalCreateTask } from '../../../../../../views/modal-create-task-new'
import { ModalAddNote } from '../../../../../../views/modal-add-notes-new'
import ChangeRegionModal from '../../modals/change-region'
import { ModalCancel } from '../../../../../../views/modal-cancel'
import { getOrdersForm } from '../../../../../../store/order/action-creators'
import { useSelector } from 'react-redux'
import { getTimeLineData } from '../../../../../../store/timeline/action-creators'
import { getStagePlanData } from '../../../../../../store/stagePlan/action-creators'
import { getVisibleFormData, getOrdersFormData } from '../../../selectors'
import { PrintPrepareModal } from '../../modals/modal-print-prepare'
import { PrintTemplateSelectModal } from '../../modals/modal-print-template-select'
import MessageCopyModal from '../../../../../../pages/card-hr/new/ui/message-copy-modal'
import { useAppDispatch } from '../../../../../../hooks/useAppDispatch/useAppDispatch'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import {
  EditOutlined,
  PlusOutlined,
  CloseOutlined,
  FileTextOutlined,
  SwapOutlined,
  PrinterOutlined,
  ColumnWidthOutlined,
} from '@ant-design/icons'
import { HSteps } from '../../../../../../ui/Steps'
import fetchAPI from '../../../../../../../lib/utils/fetch-api'
import { RetryMeasurementModal } from '../../modals/modal-retry-measurement'
import { useOrderFormSave } from '../../../order-form-save-context'

interface FormsHeaderProps {
  id: string | number
  className?: string
  stage?: number
  nextStageOnClick?: () => void
  toClaim?: () => void
  isDisabled: boolean
  withoutMontage: boolean
  setDone?: () => void
  onOpenModal?: () => void
  isStageTransitioning?: boolean
}

const titlesButton = [
  'Консультация',
  'Назначить замер',
  'Договор',
  'Отправить в производство',
  'Доставка',
  'Монтаж',
  'Выполнено',
]

const FormsHeader: React.FC<FormsHeaderProps> = memo((props: FormsHeaderProps) => {
  const {
    id,
    stage = 0,
    nextStageOnClick,
    isDisabled,
    toClaim,
    withoutMontage,
    setDone,
    onOpenModal,
    isStageTransitioning = false,
  } = props
  const isDisabledButtons = id === 'new' || isStageTransitioning
  const [isOpenModalCreateTask, setIsOpenModalCreateTask] = useState<boolean>(false)
  const [isOpenModalAddNote, setIsOpenModalAddNote] = useState<boolean>(false)
  const [isOpenModalCancel, setIsOpenModalCancel] = useState<boolean>(false)
  const [isOpenModalPrint, setIsOpenModalPrint] = useState<boolean>(false)
  const [isOpenChangeRegionModal, setIsOpenChangeRegionModal] = useState<boolean>(false)
  const [isOpenTemplateSelectModal, setIsOpenTemplateSelectModal] = useState<boolean>(false)
  const [isOpenMeasurementModal, setIsOpenMeasurementModal] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const visibleFormData = useSelector(getVisibleFormData)
  const ordersFormData = useSelector(getOrdersFormData)
  const { flushPendingSave } = useOrderFormSave()
  const [isFlushingSave, setIsFlushingSave] = useState(false)

  const handleNextStageOnClick = useCallback(async () => {
    // Если нужна модалка (стадии 1, 3, 4), открываем её
    if (onOpenModal && (stage === 1 || stage === 3 || stage === 4)) {
      onOpenModal()
    } else if (nextStageOnClick) {
      await nextStageOnClick?.()
    }
  }, [nextStageOnClick, onOpenModal, stage])

  const handleDone = useCallback(async () => {
    if (setDone) {
      await setDone?.()
    }
  }, [setDone])

  const handleCancelOnClick = useCallback(() => {
    setIsOpenModalCancel(true)
  }, [setIsOpenModalCancel, visibleFormData])

  const onCloseCancel = useCallback(() => {
    setIsOpenModalCancel(false)
  }, [setIsOpenModalCancel])

  const onCancel = useCallback(async () => {
    await dispatch(getOrdersForm(id))
    dispatch(getTimeLineData('case', id))
    dispatch(getStagePlanData('order', id))
    setIsOpenModalCancel(false)
  }, [id, setIsOpenModalCancel])

  const onCloseAddNote = useCallback(() => {
    setIsOpenModalAddNote(false)
  }, [setIsOpenModalAddNote])

  const onCloseCreateTask = useCallback(() => {
    setIsOpenModalCreateTask(false)
  }, [setIsOpenModalCreateTask])

  const onClosePrint = useCallback(() => {
    setIsOpenModalPrint(false)
    setTemplate(null)
  }, [])

  const handleChangeRegion = useCallback(() => {
    setIsOpenChangeRegionModal(true)
  }, [])

  const handleRepeatedMeasurement = useCallback(() => {
    setIsOpenMeasurementModal(true)
  }, [])

  const handleToClaim = useCallback(async () => {
    if (toClaim) {
      await toClaim?.()
    }
  }, [toClaim])

  const templates = useMemo(
    () =>
      [...(visibleFormData?.templates || ordersFormData?.templates || [])].sort((a, b) =>
        a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }),
      ),
    [visibleFormData?.templates, ordersFormData?.templates],
  )

  interface ITemplate {
    id: number
    name: string
    message?: string
    file_name?: string
  }

  const [template, setTemplate] = useState<null | ITemplate>(null)

  const flushThenOpenPrint = useCallback(
    async (open: () => void) => {
      if (isFlushingSave) {
        return
      }
      setIsFlushingSave(true)
      try {
        await flushPendingSave()
        open()
      } catch (err) {
        console.error(err)
        Modal.warning({
          title: 'Предупреждение',
          content: 'Не удалось сохранить карточку перед печатью. Попробуйте ещё раз.',
        })
      } finally {
        setIsFlushingSave(false)
      }
    },
    [flushPendingSave, isFlushingSave],
  )

  const openPrepareModal = useCallback(
    tpl => {
      void flushThenOpenPrint(() => {
        setTemplate(tpl)
        setIsOpenModalPrint(true)
      })
    },
    [flushThenOpenPrint],
  )

  const handleMobileDropdownAction = useCallback(
    (actionKey: string) => {
      if (actionKey === 'print') {
        void flushThenOpenPrint(() => {
          setIsOpenTemplateSelectModal(true)
        })
      }
    },
    [flushThenOpenPrint],
  )

  const handleTemplateSelect = useCallback(
    (selectedTemplate: any) => {
      void flushThenOpenPrint(() => {
        setTemplate(selectedTemplate)
        setIsOpenModalPrint(true)
        setIsOpenTemplateSelectModal(false)
      })
    },
    [flushThenOpenPrint],
  )

  const actionsConfig = useMemo(
    (): CardActionConfig[] => [
      {
        key: 'next-stage',
        label: titlesButton[stage] || '',
        type: 'primary',
        onClick: handleNextStageOnClick,
        isPrimary: true,
        condition: Boolean(titlesButton[stage]) && stage !== 5,
        order: 1,
        disabled: isDisabledButtons,
        loading: isStageTransitioning,
      },
      {
        key: 'done',
        label: 'Выполнено',
        type: 'primary',
        onClick: handleDone,
        isPrimary: true,
        condition: withoutMontage && stage === 5,
        order: 2,
        disabled: isDisabledButtons,
        loading: isStageTransitioning,
      },
      {
        key: 'add-note',
        label: 'Добавить заметку',
        icon: <EditOutlined />,
        onClick: () => setIsOpenModalAddNote(true),
        condition: !isDisabled,
        order: 10,
      },
      {
        key: 'create-task',
        label: 'Создать задачу',
        icon: <PlusOutlined />,
        onClick: () => setIsOpenModalCreateTask(true),
        condition: !isDisabled,
        order: 20,
      },
      {
        key: 'measurement',
        label: 'Назначить повторный замер',
        icon: <ColumnWidthOutlined />,
        onClick: handleRepeatedMeasurement,
        condition: [2, 3, 4].includes(stage),
        order: 22,
      },
      {
        key: 'change-region',
        label: 'Передать в другой офис',
        icon: <SwapOutlined />,
        onClick: handleChangeRegion,
        condition: !isDisabledButtons,
        order: 25,
      },
      {
        key: 'create-claim',
        label: 'Создать рекламацию',
        icon: <FileTextOutlined />,
        onClick: () => {
          Modal.confirm({
            title: 'Подтверждение',
            content: 'Вы уверены, что хотите создать рекламацию?',
            okText: 'Да, создать',
            cancelText: 'Отмена',
            onOk: handleToClaim,
          })
        },
        condition: true,
        order: 30,
      },
      {
        key: 'print',
        label: 'Печать',
        icon: <PrinterOutlined />,
        type: 'dropdown',
        menuItems: templates.map(template => ({
          key: `template-${template.id}`,
          label: template.name,
          onClick: () => openPrepareModal(template),
        })),
        condition: Boolean(templates?.length),
        order: 90,
      },
      {
        key: 'cancel',
        label: 'Отмена',
        icon: <CloseOutlined />,
        danger: true,
        onClick: handleCancelOnClick,
        disabled: stage === 7 || isDisabled,
        condition: !isDisabledButtons,
        order: 100,
      },
    ],
    [
      stage,
      isDisabled,
      withoutMontage,
      isDisabledButtons,
      isStageTransitioning,
      handleNextStageOnClick,
      handleDone,
      handleToClaim,
      handleCancelOnClick,
      handleChangeRegion,
      templates,
      openPrepareModal,
    ],
  )

  const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

  const handleAddMeasurement = useCallback(async modalData => {
    await fetchAPI('/api/retry_measurement', {
      method: 'POST',
      body: JSON.stringify({ ...modalData, id }),
    })
      .then(response => {
        if (response?.status === 'success') {
          setIsOpenMeasurementModal(false)
        }
      })
      .catch(e => {
        console.error({ e })
      })
  }, [])

  return (
    <>
      <HStack gap={'8'} className={'mapWrapper'} wrap>
        <div>
          {ordersFormData?.stages && (
            <HSteps
              items={Object.values(ordersFormData?.stages?.items)}
              current={ordersFormData?.stages?.current}
              history={ordersFormData?.stages?.history}
            />
          )}
        </div>
        <CardActions
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
          onMobileDropdownAction={handleMobileDropdownAction}
        />
      </HStack>

      {/* Модалка для передачи в другой офис */}
      {!isDisabledButtons && (
        <Suspense fallback={''}>
          <ChangeRegionModal
            id={+id}
            disabled={isDisabled}
            isModalOpen={isOpenChangeRegionModal}
            onCancel={() => setIsOpenChangeRegionModal(false)}
            showButton={false}
          />
        </Suspense>
      )}

      <ModalAddNote
        entityId={Number(id)}
        entityType={'case'}
        isOpen={isOpenModalAddNote}
        onClose={onCloseAddNote}
      />

      <Suspense fallback={''}>
        <ModalCreateTask
          isOpen={isOpenModalCreateTask}
          onClose={onCloseCreateTask}
          entityType={'case'}
          entityId={id}
        />
      </Suspense>

      {!isDisabledButtons && (
        <Suspense fallback={''}>
          <ModalCancel
            entityId={id}
            entityType="order"
            isOpen={isOpenModalCancel}
            onClose={onCloseCancel}
            onCancel={onCancel}
          />
        </Suspense>
      )}

      {template?.message && <MessageCopyModal id={id} template={template} onClose={onClosePrint} />}

      {template?.file_name && (
        <PrintPrepareModal
          isModalPrintOpen={isOpenModalPrint}
          onCancel={onClosePrint}
          template={template}
          id={id}
        />
      )}

      <PrintTemplateSelectModal
        isOpen={isOpenTemplateSelectModal}
        templates={templates}
        onCancel={() => setIsOpenTemplateSelectModal(false)}
        onSelectTemplate={handleTemplateSelect}
      />

      <RetryMeasurementModal
        id={id}
        isModalOpen={isOpenMeasurementModal}
        onCancel={() => setIsOpenMeasurementModal(false)}
        onOk={handleAddMeasurement}
      />
    </>
  )
})

FormsHeader.displayName = 'FormsHeader'

export default FormsHeader
